/**
 * Component Badge
 *
 * Unified colored-box icon for all PC component types.
 * Replaces the individual Logo components (CPULogo, GPULogo, etc.).
 *
 * Usage:
 *   <ComponentBadge type={ComponentType.CPU} />                     // icon only
 *   <ComponentBadge type={ComponentType.CPU} title="..." />         // icon + title
 *   <ComponentBadge type={ComponentType.CPU} title="..." subtitle="..." />
 *
 * Factory for LogoComponent prop (no JSX needed at call site):
 *   LogoComponent: makeBadgeLogo(ComponentType.CPU)
 */

import React from 'react'
import { Cpu, Gpu, MemoryStick, HardDrive, Zap, PcCase, Fan, CircuitBoard } from 'lucide-react'
import { ComponentType } from '../../models/pc-build-model'

interface BadgeEntry {
  bg: string
  mdIcon: React.ReactNode
  lgIcon: React.ReactNode
}

const BADGE_CONFIG: Record<ComponentType, BadgeEntry> = {
  [ComponentType.CPU]:         { bg: 'bg-blue-600',   mdIcon: <Cpu className="w-5 h-5" />,          lgIcon: <Cpu className="w-6 h-6" /> },
  [ComponentType.MOTHERBOARD]: { bg: 'bg-red-600',    mdIcon: <CircuitBoard className="w-5 h-5" />, lgIcon: <CircuitBoard className="w-6 h-6" /> },
  [ComponentType.GPU]:         { bg: 'bg-purple-600', mdIcon: <Gpu className="w-5 h-5" />,          lgIcon: <Gpu className="w-6 h-6" /> },
  [ComponentType.MEMORY]:      { bg: 'bg-green-600',  mdIcon: <MemoryStick className="w-5 h-5" />,  lgIcon: <MemoryStick className="w-6 h-6" /> },
  [ComponentType.STORAGE]:     { bg: 'bg-gray-600',   mdIcon: <HardDrive className="w-5 h-5" />,    lgIcon: <HardDrive className="w-6 h-6" /> },
  [ComponentType.PSU]:         { bg: 'bg-yellow-600', mdIcon: <Zap className="w-5 h-5" />,          lgIcon: <Zap className="w-6 h-6" /> },
  [ComponentType.CASE]:        { bg: 'bg-orange-600', mdIcon: <PcCase className="w-5 h-5" />,       lgIcon: <PcCase className="w-6 h-6" /> },
  [ComponentType.CPU_COOLER]:  { bg: 'bg-teal-600',   mdIcon: <Fan className="w-5 h-5" />,          lgIcon: <Fan className="w-6 h-6" /> },
}

export interface ComponentBadgeProps {
  type: ComponentType
  size?: 'md' | 'lg'
  /** bare=true: renders icon only, no colored box. Use inside already-colored containers. */
  bare?: boolean
  title?: string
  subtitle?: string
}

export const ComponentBadge: React.FC<ComponentBadgeProps> = ({ type, size = 'md', bare = false, title, subtitle }) => {
  const { bg, mdIcon, lgIcon } = BADGE_CONFIG[type]
  const icon = size === 'lg' ? lgIcon : mdIcon

  if (bare) return <>{icon}</>

  const boxClass = size === 'lg' ? 'w-16 h-16' : 'w-8 h-8'

  const badge = (
    <div className={`${boxClass} ${bg} rounded-lg flex items-center justify-center text-white shrink-0`}>
      {icon}
    </div>
  )

  if (!title) return badge

  return (
    <div className="flex items-center">
      {badge}
      <div className="ml-3 hidden sm:block">
        <div className="text-lg font-semibold text-gray-900">{title}</div>
        {subtitle && <div className="text-sm text-gray-600">{subtitle}</div>}
      </div>
    </div>
  )
}

/**
 * Factory that creates a Logo-compatible component for a given component type.
 * The returned component matches the LogoComponent prop signature used in
 * ComponentListPageTemplate and componentPageConfigs.
 */
export function makeBadgeLogo(type: ComponentType): React.FC<{ title?: string; subtitle?: string }> {
  return function BadgeLogo(props) {
    return <ComponentBadge type={type} {...props} />
  }
}

export default ComponentBadge
